// Shared visual primitives for QC cases: priority pills, status pills, the
// bug/sparkle type icon, and the deterministic module badge.
//
// Pill colors are QC-owned (per the build spec) and intentionally hardcoded —
// chrome/brand colors stay on the @wise/design-system --brand-* tokens.

import type { QcCasePriority, QcCaseStatus, QcCaseType } from '@itms-qc/shared';

// === Module badge palette ===
// 9 bg/fg pairs assigned deterministically by module slug hash so a module
// keeps its color everywhere in the app (and across sessions).
const MODULE_PALETTE: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: '#E6F1FB', fg: '#0C447C' }, // blue
  { bg: '#E1F5EE', fg: '#085041' }, // green
  { bg: '#EEEDFE', fg: '#3C3489' }, // indigo
  { bg: '#FAEEDA', fg: '#633806' }, // amber
  { bg: '#FBEAF4', fg: '#7A1F57' }, // pink
  { bg: '#E0F2F4', fg: '#055560' }, // teal
  { bg: '#EAF6E1', fg: '#3A5713' }, // lime
  { bg: '#F3EDE2', fg: '#6B4E1B' }, // sand
  { bg: '#EDEFF3', fg: '#3D4759' }, // slate
];

// djb2 — stable, cheap, good-enough spread for a handful of slugs.
function hashSlug(slug: string): number {
  let h = 5381;
  for (let i = 0; i < slug.length; i++) {
    h = (h * 33) ^ slug.charCodeAt(i);
  }
  return Math.abs(h);
}

export function moduleColors(slug: string | null): { bg: string; fg: string } {
  if (!slug) return MODULE_PALETTE[MODULE_PALETTE.length - 1];
  return MODULE_PALETTE[hashSlug(slug) % MODULE_PALETTE.length];
}

export function ModuleBadge({ slug, name }: { slug: string | null; name: string | null }) {
  const { bg, fg } = moduleColors(slug);
  return (
    <span
      style={{
        background: bg,
        color: fg,
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 4,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {name ?? slug ?? 'Unknown module'}
    </span>
  );
}

// === Priority pill (high=red, medium=amber, low=gray — per the mockup) ===
const PRIORITY_STYLES: Record<QcCasePriority, { bg: string; fg: string; label: string }> = {
  high: { bg: '#FCEBEB', fg: '#791F1F', label: 'High' },
  medium: { bg: '#FAEEDA', fg: '#633806', label: 'Med' },
  low: { bg: '#F1EFE8', fg: '#5F5E5A', label: 'Low' },
};

export function PriorityPill({ priority }: { priority: QcCasePriority }) {
  const s = PRIORITY_STYLES[priority];
  return (
    <span
      title={`Priority: ${priority}`}
      style={{
        background: s.bg,
        color: s.fg,
        fontSize: 10,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

// === Status pill ===
export const STATUS_LABELS: Record<QcCaseStatus, string> = {
  new: 'New',
  triaged: 'Triaged',
  accepted: 'Accepted',
  in_progress: 'In progress',
  fixed: 'Fixed',
  closed: 'Closed',
  wont_do: "Won't do",
  duplicate: 'Duplicate',
};

export const STATUS_STYLES: Record<QcCaseStatus, { bg: string; fg: string }> = {
  new: { bg: '#F1F2F4', fg: '#4D5560' },
  triaged: { bg: '#FAEEDA', fg: '#633806' },
  accepted: { bg: '#E0F2F4', fg: '#055560' },
  in_progress: { bg: '#E6F1FB', fg: '#0C447C' },
  fixed: { bg: '#E1F5EE', fg: '#085041' },
  closed: { bg: '#F1F2F4', fg: '#4D5560' },
  wont_do: { bg: '#F1F2F4', fg: '#4D5560' },
  duplicate: { bg: '#EEEDFE', fg: '#3C3489' },
};

export function StatusPill({ status }: { status: QcCaseStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        padding: '3px 8px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

// === Conversation court: whose turn is it? ===
// The ONE implementation of the "ball in whose court" logic (previously
// re-derived separately by the case list and the dashboard). Driven by
// status first, then last-note author. Terminal cases have no court.
export type ConversationCourt = 'awaiting_verify' | 'dev_replied' | 'awaiting_dev' | null;

export function conversationCourt(c: {
  status: QcCaseStatus;
  noteCount: number;
  lastNoteBy: string | null;
  openedBy: string;
}): ConversationCourt {
  if (['closed', 'wont_do', 'duplicate'].includes(c.status)) return null;
  if (c.status === 'fixed') return 'awaiting_verify';
  const devRepliedLast =
    c.noteCount > 0 && !!c.lastNoteBy && c.lastNoteBy.toLowerCase() !== c.openedBy.toLowerCase();
  if (devRepliedLast) return 'dev_replied';
  // In progress / accepted speak for themselves (the status pill carries the
  // state) unless the reporter has since replied.
  if (c.status === 'in_progress' || c.status === 'accepted') return null;
  return 'awaiting_dev';
}

const COURT_STYLES: Record<Exclude<ConversationCourt, null>, { fg: string; bg: string; label: string }> = {
  awaiting_verify: { fg: '#1B7A3D', bg: '#DFF3E6', label: '● Awaiting verify' },
  dev_replied: { fg: '#085041', bg: '#E1F5EE', label: '✓ Dev replied' },
  awaiting_dev: { fg: '#8A5A00', bg: '#FBEFD3', label: '● Awaiting dev' },
};

export function CourtChip({ court }: { court: ConversationCourt }) {
  if (!court) return null;
  const s = COURT_STYLES[court];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: s.fg,
        background: s.bg,
        borderRadius: 10,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

// === Type icon: bug vs sparkle (feature request) ===
export function TypeIcon({ type, size = 16 }: { type: QcCaseType; size?: number }) {
  const color = 'var(--brand-text-muted, #8BA4B8)';
  const title = type === 'bug' ? 'Bug' : 'Feature request';
  if (type === 'bug') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-label={title}
      >
        <title>{title}</title>
        {/* tabler-style bug */}
        <path d="M9 9V8a3 3 0 0 1 6 0v1" />
        <path d="M8 9h8a6 6 0 0 1 1 3v3a5 5 0 0 1-10 0v-3a6 6 0 0 1 1-3" />
        <line x1="3" y1="13" x2="7" y2="13" />
        <line x1="17" y1="13" x2="21" y2="13" />
        <line x1="12" y1="20" x2="12" y2="14" />
        <line x1="4" y1="19" x2="7.35" y2="17" />
        <line x1="20" y1="19" x2="16.65" y2="17" />
        <line x1="4" y1="7" x2="7" y2="9" />
        <line x1="20" y1="7" x2="17" y2="9" />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      stroke="none"
      aria-label={title}
    >
      <title>{title}</title>
      {/* four-point sparkle + companion star */}
      <path d="M10 3l1.7 4.8L16.5 9.5l-4.8 1.7L10 16l-1.7-4.8L3.5 9.5l4.8-1.7L10 3z" />
      <path d="M18 13l1 2.7 2.7 1-2.7 1-1 2.7-1-2.7-2.7-1 2.7-1 1-2.7z" />
    </svg>
  );
}
